import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import { Table, Button, Input, message } from "antd";
import moment from "moment";
import AddEditExpensePopup from "./components/popups/AddExpensesPopup";
import axios from "axios";
import debounce from "lodash/debounce";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { ArrowDownCircle, ArrowUpCircle } from "react-feather";

const { Search } = Input;

const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";

const DayBook = () => {
  const [transactions, setTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [stats, setStats] = useState({
    upi_collection: 0,
    bank_transfer: 0,
    cash_collection: 0,
    company_billing: 0, // Added company_billing
    expenses: 0,
    cash_on_drawer: 0,
  });
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [paymentModeFilter, setPaymentModeFilter] = useState(null);
  const [isExpensePopupVisible, setIsExpensePopupVisible] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);

  const getBusinessDate = () => {
    const storedBusinessDate = localStorage.getItem("business_date");
    return storedBusinessDate ? moment(storedBusinessDate) : moment();
  };

  const [selectedDate, setSelectedDate] = useState(getBusinessDate());

  useEffect(() => {
    fetchTransactions();
  }, [selectedDate]);

  useEffect(() => {
    const handleStorageChange = () => {
      const newBusinessDate = localStorage.getItem("business_date");
      if (newBusinessDate) {
        const newDate = moment(newBusinessDate);
        if (!newDate.isSame(selectedDate, "day")) {
          setSelectedDate(newDate);
        }
      } else {
        setSelectedDate(moment());
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [selectedDate]);

  const fetchTransactions = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");

    try {
      const response = await axios.get(`${apiURL}/transactions/daybook`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date: selectedDate.format("YYYY-MM-DD") },
      });
      if (response.data.success) {
        const formattedTransactions = (response.data.transactions || []).map((txn) => ({
          ...txn,
          amount: Number(txn.amount) || 0,
        }));
        setTransactions(formattedTransactions);
        setAllTransactions(formattedTransactions);
        setStats({
          upi_collection: Number(response.data.stats?.upi_collection) || 0,
          bank_transfer: Number(response.data.stats?.bank_transfer) || 0,
          cash_collection: Number(response.data.stats?.cash_collection) || 0,
          company_billing: Number(response.data.stats?.company_billing) || 0, // Added company_billing
          expenses: Number(response.data.stats?.expenses) || 0,
          cash_on_drawer: Number(response.data.stats?.cash_on_drawer) || 0,
        });
      } else {
        Swal.fire({
          title: "Error!",
          text: "Failed to fetch transactions",
          icon: "error",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "Failed to fetch transactions",
        icon: "error",
      });
      setTransactions([]);
      setAllTransactions([]);
      setStats({ 
        upi_collection: 0, 
        bank_transfer: 0, 
        cash_collection: 0, 
        company_billing: 0, // Added company_billing
        expenses: 0, 
        cash_on_drawer: 0 
      });
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = debounce((value) => setSearchText(value), 300);

  const filteredTransactions = useMemo(() => {
    let filtered = [...allTransactions];
    if (searchText) {
      filtered = filtered.filter((txn) =>
        `${txn.fullname || ''} ${txn.category || ''} ${txn.payment_reference || ''}`
          .toLowerCase()
          .includes(searchText.toLowerCase())
      );
    }
    if (paymentModeFilter) {
      filtered = filtered.filter((txn) => txn.payment_mode === paymentModeFilter);
    }
    return filtered;
  }, [allTransactions, searchText, paymentModeFilter]);

  const cashInTransactions = useMemo(() => 
    filteredTransactions.filter((txn) => txn.transaction_type === "CASH_IN"), 
    [filteredTransactions]
  );
  const cashOutTransactions = useMemo(() => 
    filteredTransactions.filter((txn) => txn.transaction_type === "CASH_OUT"), 
    [filteredTransactions]
  );

  const cashInTotal = cashInTransactions.reduce((sum, txn) => sum + txn.amount, 0);
  const cashOutTotal = cashOutTransactions.reduce((sum, txn) => sum + txn.amount, 0);

  // Ensure a minimum of 10 rows in each table
  const MIN_ROWS = 10; // Minimum rows to display
  const maxRows = Math.max(Math.max(cashInTransactions.length, cashOutTransactions.length), MIN_ROWS); // At least 10 rows
  const balancedCashInTransactions = [...cashInTransactions];
  const balancedCashOutTransactions = [...cashOutTransactions];

  // Add empty rows to ensure at least 10 rows in each table
  for (let i = cashInTransactions.length; i < maxRows; i++) {
    balancedCashInTransactions.push({ key: `empty-in-${i}`, category: "", payment_mode: "", amount: "", payment_reference: "" });
  }
  for (let i = cashOutTransactions.length; i < maxRows; i++) {
    balancedCashOutTransactions.push({ key: `empty-out-${i}`, category: "", payment_mode: "", amount: "", payment_reference: "" });
  }

  const handleAddOrEditExpense = (data) => {
    const { expense, isEdit } = data;
    const token = localStorage.getItem("token");

    const newOrUpdatedExpense = { 
      ...expense, 
      amount: Number(expense.amount) || 0, 
      transaction_id: expense.transaction_id || Date.now()
    };

    if (!isEdit) {
      setAllTransactions([newOrUpdatedExpense, ...allTransactions]);
      setTransactions([newOrUpdatedExpense, ...transactions]);
    } else {
      const updatedTransactions = allTransactions.map((txn) =>
        txn.transaction_id === newOrUpdatedExpense.transaction_id ? newOrUpdatedExpense : txn
      );
      setAllTransactions(updatedTransactions);
      setTransactions(updatedTransactions);
    }

    setIsExpensePopupVisible(false);
    setSelectedExpense(null);

    axios.post(
      isEdit ? `${apiURL}/transactions/edit` : `${apiURL}/transactions/add-expenses`,
      newOrUpdatedExpense,
      { headers: { Authorization: `Bearer ${token}` } }
    ).then(() => {
      message.success(`Expense ${isEdit ? 'updated' : 'added'} successfully!`);
      fetchStats();
    }).catch((error) => {
      message.error(`Failed to ${isEdit ? 'update' : 'add'} expense. Reverting changes...`);
      console.error(`Error ${isEdit ? 'updating' : 'adding'} expense:`, error);
      fetchTransactions();
    });
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${apiURL}/transactions/daybook`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date: selectedDate.format("YYYY-MM-DD") },
      });
      if (response.data.success) {
        setStats({
          upi_collection: Number(response.data.stats?.upi_collection) || 0,
          bank_transfer: Number(response.data.stats?.bank_transfer) || 0,
          cash_collection: Number(response.data.stats?.cash_collection) || 0,
          company_billing: Number(response.data.stats?.company_billing) || 0,
          expenses: Number(response.data.stats?.expenses) || 0,
          cash_on_drawer: Number(response.data.stats?.cash_on_drawer) || 0,
        });
      }
    } catch (error) {
      message.error("Error fetching stats.");
    }
  };

  const handleDateChange = (direction) => {
    setSelectedDate((prevDate) =>
      direction === "prev" ? prevDate.clone().subtract(1, "days") : prevDate.clone().add(1, "days")
    );
  };

  const commonColumns = [
    { 
      title: "Particulars", 
      dataIndex: "category", 
      key: "particulars", 
      render: (text, record) => {
        if (!record.category && !record.fullname && !record.source) return ""; // Return empty string for empty rows
        return `${record.payment_reference	 || ''} - ${text || record.source || ''}`;
      }
    },
    { title: "Mode", dataIndex: "payment_mode", key: "payment_mode", render: (text) => text || "" },
    { title: "Amount", dataIndex: "amount", key: "amount", render: (text) => text ? `₹${Number(text).toFixed(2)}` : "" },
    { title: "Reference", dataIndex: "payment_reference", key: "payment_reference", render: (text) => text || "" },
  ];

  const cashOutColumns = [
    ...commonColumns,
    {
      title: "Actions",
      key: "actions",
      render: (text, record) =>
        record.source === "EXPENSE" ? (
          <Button
            type="link"
            onClick={() => {
              setSelectedExpense(record);
              setIsExpensePopupVisible(true);
            }}
            style={{ padding: 0 }}
          >
            Edit
          </Button>
        ) : null,
    },
  ];

  const dayBookStats = [
    { label: "UPI Collection", value: `₹${Number(stats.upi_collection || 0).toFixed(2)}`, color: "#007bff" },
    { label: "Bank Transfer", value: `₹${Number(stats.bank_transfer || 0).toFixed(2)}`, color: "#17a2b8" },
    { label: "Cash Collection", value: `₹${Number(stats.cash_collection || 0).toFixed(2)}`, color: "#28a745" },
    { label: "Company Credit", value: `₹${Number(stats.company_billing || 0).toFixed(2)}`, color: "#6f42c1" }, // Added Company Credit
    { label: "Expenses", value: `₹${Number(stats.expenses || 0).toFixed(2)}`, color: "#dc3545" },
    { label: "Cash on Drawer", value: `₹${Number(stats.cash_on_drawer || 0).toFixed(2)}`, color: "#ffc107" },
  ];

  const paymentModes = [
    { value: "CASH", label: "Cash" },
    { value: "UPI", label: "UPI" },
    { value: "BANK_TRANSFER", label: "Bank Transfer" },
    { value: "CARD", label: "Card" },
    { value: "CHEQUE", label: "Cheque" },
    { value: "COMPANY", label: "Company Credit" }, // Added Company Credit
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header page-top-icons" style={{ background: "#f8f9fa", padding: "15px 20px", borderRadius: "8px" }}>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <h2 className="mb-0 d-flex align-items-center">
              <i className="fas fa-book text-primary me-2" style={{ fontSize: "22px" }}></i>
              Day Book
            </h2>
            <div className="d-flex align-items-center">
              <Button
                onClick={() => handleDateChange("prev")}
                className="btn btn-outline-primary btn-sm rounded-circle me-2"
                style={{ width: "34px", height: "34px" }}
              >
                <i className="fas fa-chevron-left"></i>
              </Button>
              <span className="fs-5 fw-bold">{selectedDate.format("DD MMM YYYY")}</span>
              <Button
                onClick={() => handleDateChange("next")}
                className="btn btn-outline-primary btn-sm rounded-circle ms-2"
                style={{ width: "34px", height: "34px" }}
              >
                <i className="fas fa-chevron-right"></i>
              </Button>
            </div>
            <Select
              options={paymentModes}
              placeholder="Filter by Payment Mode"
              isClearable
              styles={{
                container: (provided) => ({ ...provided, width: 250 }),
                control: (provided) => ({ ...provided, minHeight: "38px", borderRadius: "4px", borderColor: "#d9d9d9", background: "#fff" }),
                menu: (provided) => ({ ...provided, width: 250 }),
              }}
              onChange={(option) => setPaymentModeFilter(option ? option.value : null)}
            />
            <Search
              placeholder="Search by Particulars or Reference"
              allowClear
              enterButton="Search"
              size="large"
              onChange={(e) => debouncedSearch(e.target.value)}
              style={{ maxWidth: 450 }}
            />
            <Link to="#" className="list-view btn-linkcuston" id="collapse-header">
              <i className="las la-expand-arrows-alt" />
            </Link>
            <Link to="#" className="list-view btn btn-link" data-bs-toggle="modal" data-bs-target="#export">
              <i className="las la-file-export" />
            </Link>
            <Button
              className="btn add-btn"
              size="large"
              icon={<i className="fas fa-plus me-2"></i>}
              onClick={() => {
                setSelectedExpense(null);
                setIsExpensePopupVisible(true);
              }}
            >
              Add Expense
            </Button>
          </div>
        </div>

        <div className="row g-4 mb-4" style={{ padding: "5px 10px" }}>
          {dayBookStats.map((stat, index) => (
            <div key={index} className="col-md-2 col-6 stats-box" style={{ margin: "0 15px", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", padding: "10px" }}>
              <div className="stats-border" style={{ backgroundColor: stat.color }}></div>
              <div className="stats-content">
                <h2 style={{ fontSize: "1.5rem", margin: 0 }}>{stat.value}</h2>
                <p style={{ margin: 0, color: "#666" }}>{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="text-center my-5">
            <div className="loader">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
        ) : (
          <div className="row g-0">
            <div className="col-md-6">
              <div className="card" style={{ borderRadius: "0", borderRight: "none", overflowX: "auto", border: "1px solid #dee2e6", borderBottom: "none" }}>
                <div
                  className="card-header"
                  style={{
                    backgroundColor: "#fff",
                    padding: "12px 16px",
                    borderBottom: "none",
                    position: "relative",
                  }}
                >
                  <h5
                    className="mb-0 d-flex align-items-center"
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#333",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    <ArrowDownCircle size={18} color="#28a745" style={{ marginRight: "8px" }} />
                    Transaction In Flow (Total: ₹{cashInTotal.toFixed(2)})
                  </h5>
                  <div
                    style={{
                      position: "absolute",
                      bottom: "0",
                      left: "0",
                      width: "100%",
                      height: "4px",
                      backgroundColor: "#28a745",
                    }}
                  />
                </div>
                <div className="card-body p-0">
                  <Table
                    dataSource={balancedCashInTransactions}
                    columns={commonColumns}
                    rowKey={(record) => record.transaction_id || record.key}
                    bordered
                    pagination={false}
                    className="daybook-table"
                    style={{ margin: 0 }}
                    summary={() => (
                      <Table.Summary.Row style={{ backgroundColor: "#f8f9fa" }}>
                        <Table.Summary.Cell colSpan={3} style={{ fontWeight: 600, fontSize: "14px" }}>
                          Total In Flow
                        </Table.Summary.Cell>
                        <Table.Summary.Cell style={{ fontWeight: 600, fontSize: "14px" }}>
                          ₹{cashInTotal.toFixed(2)}
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card" style={{ borderRadius: "0", overflowX: "auto", border: "1px solid #dee2e6", borderBottom: "none" }}>
                <div
                  className="card-header"
                  style={{
                    backgroundColor: "#fff",
                    padding: "12px 16px",
                    borderBottom: "none",
                    position: "relative",
                  }}
                >
                  <h5
                    className="mb-0 d-flex align-items-center"
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#333",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    <ArrowUpCircle size={18} color="#dc3545" style={{ marginRight: "8px" }} />
                    Transaction Out Flow (Total: ₹{cashOutTotal.toFixed(2)})
                  </h5>
                  <div
                    style={{
                      position: "absolute",
                      bottom: "0",
                      left: "0",
                      width: "100%",
                      height: "4px",
                      backgroundColor: "#dc3545",
                    }}
                  />
                </div>
                <div className="card-body p-0">
                  <Table
                    dataSource={balancedCashOutTransactions}
                    columns={cashOutColumns}
                    rowKey={(record) => record.transaction_id || record.key}
                    bordered
                    pagination={false}
                    className="daybook-table"
                    style={{ margin: 0 }}
                    summary={() => (
                      <Table.Summary.Row style={{ backgroundColor: "#f8f9fa" }}>
                        <Table.Summary.Cell colSpan={4} style={{ fontWeight: 600, fontSize: "14px" }}>
                          Total Out Flow
                        </Table.Summary.Cell>
                        <Table.Summary.Cell style={{ fontWeight: 600, fontSize: "14px" }}>
                          ₹{cashOutTotal.toFixed(2)}
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <style>
          {`
            .daybook-table .ant-table-thead > tr > th {
              background: #ffffff;
              font-weight: 600;
              padding: 8px 16px;
              font-size: 14px;
            }

            .daybook-table .ant-table-tbody > tr > td {
              padding: 12px 18px;
              font-size: 14px;
              height: 60px;
            }

            .daybook-table .ant-table {
              border: 1px solid #dee2e6;
            }

            .daybook-table .ant-table-tbody > tr.ant-table-row:hover > td {
              background: #f5f5f5;
            }
          `}
        </style>

        {isExpensePopupVisible && (
          <AddEditExpensePopup
            onSubmit={handleAddOrEditExpense}
            expenseData={selectedExpense}
            onCancel={() => {
              setIsExpensePopupVisible(false);
              setSelectedExpense(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default DayBook;